/**
 * Invite Friends Button component
 * Small floating button in top right with "Invite" / "Create" text.
 * Theme-aware: readable and fits both light and dark themes.
 */

import React, { useMemo } from 'react'
import { Pressable, Platform, Text } from 'react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations'
import { UserPlus } from 'lucide-react-native'
import { useTheme } from '../../src/theme/useTheme'
import { useThemeContext } from '../../src/theme/ThemeContext'
import { getInviteFriendsButtonStyles } from './InviteFriendsButton.styles'

interface InviteFriendsButtonProps {
  onPress: () => void
  label?: string // Optional label, defaults to "Invite"
  /** Use white icon/text and light border for use on dark backgrounds (e.g. gradient hero) */
  variant?: 'default' | 'light'
  /** Test ID for testing */
  testID?: string
}

export const InviteFriendsButton: React.FC<InviteFriendsButtonProps> = ({
  onPress,
  label = 'Invite',
  variant = 'default',
  testID,
}) => {
  const theme = useTheme()
  const { themeMode } = useThemeContext()
  const styles = useMemo(
    () => getInviteFriendsButtonStyles(theme, themeMode, variant),
    [theme, themeMode, variant]
  )
  const iconColor = variant === 'light' ? theme.white : theme.primary

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed, hovered }) => [
        styles.button,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.buttonPressed,
        hovered && Platform.OS === 'web' && styles.buttonHover,
      ]}
      accessibilityRole="button"
      accessibilityLabel={label === 'Create' ? 'Create group' : 'Invite friends'}
      testID={testID || 'invite-friends-button'}
    >
      <UserPlus size={18} color={iconColor} />
      <Text style={styles.buttonText}>{label}</Text>
    </Pressable>
  )
}

